import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Camera, 
  MapPin, 
  Sparkles, 
  ArrowLeft, 
  Upload, 
  X, 
  Target,
  Flag,
  Loader2,
  DollarSign,
  Tag,
  Copy,
  Check,
  ExternalLink,
  ShoppingBag,
  Download,
  Save,
  FileSpreadsheet,
  Calendar,
  MapPinned,
  ChevronDown,
  Mail,
  MessageCircle,
  Share2,
  Clipboard
} from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

/**
 * AI Scorecard Scanner Tool
 * Allows users to scan scorecards and extract scores via AI
 */
const ScorecardScannerTool = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [numberOfHoles, setNumberOfHoles] = useState(18);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [csvContent, setCsvContent] = useState('');
  const [csvFileName, setCsvFileName] = useState('');
  const [selectedPlayers, setSelectedPlayers] = useState([]);  // Array of indices
  const [saving, setSaving] = useState(false);
  
  // Export options
  const [exportLayout, setExportLayout] = useState('horizontal'); // 'horizontal' or 'vertical'
  
  // Save form fields
  const [courseName, setCourseName] = useState('');
  const [courseSearch, setCourseSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [playDate, setPlayDate] = useState(new Date().toISOString().split('T')[0]);
  const [tees, setTees] = useState('white');
  const [notes, setNotes] = useState('');
  
  // Refs for iOS Safari camera compatibility
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Initialize selected players when result changes
  useEffect(() => {
    if (result?.players?.length) {
      setSelectedPlayers([0]); // Select first player by default
    }
  }, [result]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
      setResult(null);
      setShowSaveForm(false);
      setShowExportOptions(false);
      setSelectedPlayers([]);
    }
    e.target.value = '';
  };

  const analyzeScorecard = async () => {
    if (!file) return;
    
    setAnalyzing(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('holes', numberOfHoles.toString());

    try {
      const response = await axios.post(`${API}/scan-scorecard`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message || 'Scorecard analyzed successfully!');
      } else {
        toast.error(response.data.message || 'Could not analyze scorecard');
      }
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('Failed to analyze scorecard. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const clearScan = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setShowSaveForm(false);
    setShowExportOptions(false);
    setShowShareOptions(false);
    setCsvContent('');
    setCsvFileName('');
    setSelectedCourse(null);
    setCourseName('');
    setCourseSearch('');
    setSelectedPlayers([]);
  };

  // Toggle player selection
  const togglePlayerSelection = (index) => {
    setSelectedPlayers(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index].sort((a, b) => a - b);
      }
    });
  };

  // Select all players
  const selectAllPlayers = () => {
    if (result?.players?.length) {
      setSelectedPlayers(result.players.map((_, idx) => idx));
    }
  };

  // Deselect all players
  const deselectAllPlayers = () => {
    setSelectedPlayers([]);
  };

  // Check if all players are selected
  const allPlayersSelected = result?.players?.length === selectedPlayers.length;

  // Search for courses
  const searchCourses = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/courses/search?q=${encodeURIComponent(query)}`);
      if (response.data.courses) {
        setSearchResults(response.data.courses.slice(0, 5));
      }
    } catch (error) {
      console.error('Course search error:', error);
    }
  };

  // Generate CSV content and show share options
  const generateCSV = () => {
    if (!result?.players?.length || selectedPlayers.length === 0) {
      toast.error('Please select at least one player to export');
      return;
    }
    
    const players = selectedPlayers.map(idx => result.players[idx]);
    const courseInfo = selectedCourse?.name || courseName || 'Golf Course';
    let csv = '';
    
    if (exportLayout === 'horizontal') {
      // Horizontal layout: Players as columns
      csv += `SCORECARD EXPORT\n`;
      csv += `Course,${courseInfo}\n`;
      csv += `Date,${playDate}\n`;
      csv += `Holes,${numberOfHoles}\n\n`;
      
      // Player names header
      csv += `Hole,${players.map(p => p.name || 'Player').join(',')}\n`;
      
      // Scores for each hole
      for (let hole = 0; hole < numberOfHoles; hole++) {
        csv += `${hole + 1},${players.map(p => p.scores?.[hole] || '').join(',')}\n`;
      }
      
      // Front 9 total (if 18 holes)
      if (numberOfHoles === 18) {
        csv += `OUT,${players.map(p => {
          const front9 = p.scores?.slice(0, 9).reduce((a, b) => a + (parseInt(b) || 0), 0);
          return front9 || '';
        }).join(',')}\n`;
      }
      
      // Back 9 total (if 18 holes)
      if (numberOfHoles === 18) {
        csv += `IN,${players.map(p => {
          const back9 = p.scores?.slice(9, 18).reduce((a, b) => a + (parseInt(b) || 0), 0);
          return back9 || '';
        }).join(',')}\n`;
      }
      
      // Total row
      csv += `TOTAL,${players.map(p => p.total || p.scores?.reduce((a, b) => a + (parseInt(b) || 0), 0) || '').join(',')}\n`;
      
    } else {
      // Vertical layout: Each player as a separate section
      csv += `SCORECARD EXPORT\n`;
      csv += `Course,${courseInfo}\n`;
      csv += `Date,${playDate}\n`;
      csv += `Holes,${numberOfHoles}\n\n`;
      
      players.forEach((player, pIdx) => {
        if (pIdx > 0) csv += '\n';
        csv += `PLAYER,${player.name || `Player ${selectedPlayers[pIdx] + 1}`}\n`;
        csv += `Hole,Score\n`;
        
        player.scores?.slice(0, numberOfHoles).forEach((score, holeIdx) => {
          csv += `${holeIdx + 1},${score || ''}\n`;
        });
        
        if (numberOfHoles === 18) {
          const front9 = player.scores?.slice(0, 9).reduce((a, b) => a + (parseInt(b) || 0), 0);
          const back9 = player.scores?.slice(9, 18).reduce((a, b) => a + (parseInt(b) || 0), 0);
          csv += `OUT,${front9 || ''}\n`;
          csv += `IN,${back9 || ''}\n`;
        }
        
        csv += `TOTAL,${player.total || player.scores?.reduce((a, b) => a + (parseInt(b) || 0), 0) || ''}\n`;
      });
    }
    
    const playerNames = players.length === 1 
      ? (players[0].name || 'player').replace(/\s+/g, '_')
      : `${players.length}_players`;
    const fileName = `scorecard_${playDate}_${playerNames}.csv`;
    
    setCsvContent(csv);
    setCsvFileName(fileName);
    setShowExportOptions(false);
    setShowShareOptions(true);
  };

  // Download CSV file
  const downloadCSV = () => {
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = csvFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Scorecard downloaded!');
  };

  // Share via native share API (works on mobile)
  const shareNative = async () => {
    try {
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const file = new File([blob], csvFileName, { type: 'text/csv' });
      
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Golf Scorecard',
          text: `Golf scorecard from ${playDate}`
        });
        toast.success('Shared successfully!');
      } else {
        // Fallback - try sharing just the text
        if (navigator.share) {
          await navigator.share({
            title: 'Golf Scorecard',
            text: csvContent
          });
          toast.success('Shared successfully!');
        } else {
          toast.error('Sharing not supported on this device');
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
        toast.error('Could not share. Try downloading instead.');
      }
    }
  };

  // Share via email
  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Golf Scorecard - ${playDate}`);
    const body = encodeURIComponent(`Golf Scorecard Export\n\n${csvContent}\n\nExported from Par Seeker`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    toast.success('Opening email...');
  };

  // Share via SMS/Text
  const shareViaSMS = () => {
    // Create a shorter summary for SMS
    const players = selectedPlayers.map(idx => result.players[idx]);
    let summary = `Golf Scorecard - ${playDate}\n`;
    players.forEach(p => {
      summary += `${p.name || 'Player'}: ${p.total || '?'}\n`;
    });
    summary += `\nFull details in Par Seeker app`;
    
    const body = encodeURIComponent(summary);
    // sms: works on both iOS and Android
    window.location.href = `sms:?body=${body}`;
    toast.success('Opening messages...');
  };

  // Copy to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(csvContent).then(() => {
      toast.success('Copied to clipboard!');
    }).catch(() => {
      toast.error('Could not copy to clipboard');
    });
  };

  // Save round to handicap (saves first selected player)
  const saveToHandicap = async () => {
    if (!result?.players?.length || selectedPlayers.length === 0) {
      toast.error('Please select a player to save');
      return;
    }
    
    const playerIndex = selectedPlayers[0]; // Save first selected player
    const player = result.players[playerIndex];
    const scores = player.scores || [];
    
    if (!selectedCourse && !courseName) {
      toast.error('Please select or enter a course name');
      return;
    }

    setSaving(true);
    try {
      const scoreData = {
        course_name: selectedCourse?.name || courseName,
        course_id: selectedCourse?.id || null,
        date: playDate,
        tees: tees,
        holes: numberOfHoles,
        scores: scores.map(s => parseInt(s) || 0),
        total: player.total || scores.reduce((a, b) => a + (parseInt(b) || 0), 0),
        notes: notes,
        source: 'ai_scanner'
      };

      const response = await axios.post(`${API}/scores`, scoreData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data) {
        toast.success('Round saved to your handicap!');
        setShowSaveForm(false);
        navigate('/history');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save round. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="scorecard-scanner-tool">
      {/* Hidden file inputs for iOS compatibility */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
        data-testid="scanner-camera-input"
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
        data-testid="scanner-gallery-input"
      />
      
      {/* Tool Header with Par Seeker Logo */}
      <div className="text-center">
        <img 
          src="/images/par-seeker-logo.png" 
          alt="Par Seeker" 
          className="w-20 h-20 object-contain mx-auto mb-3"
        />
        <h2 className="text-xl font-bold text-[var(--ps-text-primary)]">AI Scorecard Scanner</h2>
        <p className="text-sm text-[var(--ps-text-secondary)] mt-1">
          Take a photo of your scorecard and let AI extract your scores
        </p>
      </div>

      {/* Hole Selection */}
      <div className="flex justify-center gap-2">
        <button
          onClick={() => setNumberOfHoles(9)}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            numberOfHoles === 9 
              ? 'bg-[var(--ps-primary)] text-white' 
              : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-hover)]'
          }`}
        >
          9 Holes
        </button>
        <button
          onClick={() => setNumberOfHoles(18)}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            numberOfHoles === 18 
              ? 'bg-[var(--ps-primary)] text-white' 
              : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-hover)]'
          }`}
        >
          18 Holes
        </button>
      </div>

      {!preview ? (
        <div className="space-y-3">
          {/* Take Photo */}
          <button
            onClick={() => cameraInputRef.current?.click()}
            className="w-full border-2 border-dashed border-[var(--ps-primary)]/50 rounded-xl p-8 text-center hover:border-[var(--ps-primary)] hover:bg-[var(--ps-primary)]/5 transition-colors"
            data-testid="take-photo-btn"
          >
            <Camera className="w-12 h-12 mx-auto mb-3 text-[var(--ps-primary)]" />
            <p className="text-lg font-semibold text-[var(--ps-primary)]">Take Photo</p>
            <p className="text-sm text-[var(--ps-text-secondary)]">Snap your scorecard</p>
          </button>
          
          {/* Upload from Gallery */}
          <button
            onClick={() => galleryInputRef.current?.click()}
            className="w-full border-2 border-dashed border-[var(--ps-border)] rounded-xl p-6 text-center hover:border-[var(--ps-text-muted)] hover:bg-[var(--ps-bg-elevated)] transition-colors"
            data-testid="upload-gallery-btn"
          >
            <Upload className="w-8 h-8 mx-auto mb-2 text-[var(--ps-text-muted)]" />
            <p className="text-base font-medium text-[var(--ps-text-secondary)]">Upload from Gallery</p>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Preview Image */}
          <div className="relative">
            <img 
              src={preview} 
              alt="Scorecard preview" 
              className="w-full rounded-xl border-2 border-[var(--ps-border)] max-h-72 object-contain bg-[var(--ps-bg-elevated)]" 
            />
            <button
              onClick={clearScan}
              className="absolute top-2 right-2 p-2 bg-[var(--ps-error)] text-white rounded-full hover:opacity-90"
              data-testid="clear-scanner-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Analyze Button */}
          {!result && (
            <Button
              onClick={analyzeScorecard}
              disabled={analyzing}
              className="w-full h-14 text-lg font-bold bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)]"
              data-testid="analyze-scorecard-btn"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="w-6 h-6 mr-2" />
                  Analyze Scorecard
                </>
              )}
            </Button>
          )}
          
          {/* Results - Main View */}
          {result && result.success && !showSaveForm && !showExportOptions && !showShareOptions && (
            <div className="bg-[var(--ps-bg-surface)] rounded-xl border border-[var(--ps-border)] p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[var(--ps-success)]">
                  <Sparkles className="w-5 h-5" />
                  <span className="font-bold">Scores Detected!</span>
                </div>
                <span className="text-sm text-[var(--ps-text-muted)]">
                  {result.players?.length || 0} player{result.players?.length !== 1 ? 's' : ''} found
                </span>
              </div>
              
              {/* Multi-Player Selection */}
              {result.players?.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[var(--ps-text-secondary)]">Select Players:</span>
                    <button
                      onClick={allPlayersSelected ? deselectAllPlayers : selectAllPlayers}
                      className="text-xs text-[var(--ps-primary)] hover:underline"
                    >
                      {allPlayersSelected ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  
                  {/* Player Cards with Checkboxes */}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {result.players.map((player, idx) => (
                      <div 
                        key={idx}
                        onClick={() => togglePlayerSelection(idx)}
                        className={`bg-[var(--ps-bg-elevated)] rounded-lg p-3 cursor-pointer border-2 transition-colors ${
                          selectedPlayers.includes(idx) 
                            ? 'border-[var(--ps-primary)]' 
                            : 'border-transparent hover:border-[var(--ps-border)]'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {/* Checkbox */}
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            selectedPlayers.includes(idx)
                              ? 'bg-[var(--ps-primary)] border-[var(--ps-primary)]'
                              : 'border-[var(--ps-border)]'
                          }`}>
                            {selectedPlayers.includes(idx) && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          
                          {/* Player Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-[var(--ps-text-primary)] truncate">
                                {player.name || `Player ${idx + 1}`}
                              </span>
                              <span className="text-xl font-black text-[var(--ps-primary)] ml-2">
                                {player.total || '—'}
                              </span>
                            </div>
                            
                            {/* Mini score preview */}
                            <div className="flex gap-1 mt-1 overflow-x-auto">
                              {player.scores?.slice(0, numberOfHoles).map((score, holeIdx) => (
                                <span 
                                  key={holeIdx} 
                                  className="text-xs text-[var(--ps-text-muted)] bg-[var(--ps-bg-surface)] px-1 rounded"
                                >
                                  {score || '-'}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Selected count indicator */}
              {selectedPlayers.length > 0 && (
                <div className="text-sm text-[var(--ps-text-muted)] text-center">
                  {selectedPlayers.length} player{selectedPlayers.length !== 1 ? 's' : ''} selected
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowExportOptions(true)}
                  disabled={selectedPlayers.length === 0}
                  className="border-[var(--ps-border)] text-[var(--ps-text-secondary)]"
                  data-testid="export-csv-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-1" />
                  Export CSV
                </Button>
                <Button
                  onClick={() => setShowSaveForm(true)}
                  disabled={selectedPlayers.length === 0}
                  className="bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)]"
                  data-testid="save-to-handicap-btn"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save Round
                </Button>
              </div>
              
              <Button
                variant="outline"
                onClick={clearScan}
                className="w-full border-[var(--ps-border)] text-[var(--ps-text-secondary)]"
              >
                Scan Another
              </Button>
            </div>
          )}
          
          {/* Export Options Panel */}
          {result && result.success && showExportOptions && (
            <div className="bg-[var(--ps-bg-surface)] rounded-xl border border-[var(--ps-border)] p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[var(--ps-primary)]">
                  <FileSpreadsheet className="w-5 h-5" />
                  <span className="font-bold">Export Options</span>
                </div>
                <button
                  onClick={() => setShowExportOptions(false)}
                  className="p-1 hover:bg-[var(--ps-bg-hover)] rounded"
                >
                  <X className="w-5 h-5 text-[var(--ps-text-muted)]" />
                </button>
              </div>
              
              {/* Players to Export */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[var(--ps-text-secondary)]">
                    Players to Export ({selectedPlayers.length})
                  </span>
                  <button
                    onClick={allPlayersSelected ? deselectAllPlayers : selectAllPlayers}
                    className="text-xs text-[var(--ps-primary)] hover:underline"
                  >
                    {allPlayersSelected ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {result.players?.map((player, idx) => (
                    <button
                      key={idx}
                      onClick={() => togglePlayerSelection(idx)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        selectedPlayers.includes(idx)
                          ? 'bg-[var(--ps-primary)] text-white'
                          : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-hover)]'
                      }`}
                    >
                      {player.name || `Player ${idx + 1}`}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Layout Selection */}
              <div>
                <span className="text-sm font-medium text-[var(--ps-text-secondary)] block mb-2">Layout Format:</span>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setExportLayout('horizontal')}
                    className={`p-3 rounded-lg border-2 text-center transition-colors ${
                      exportLayout === 'horizontal'
                        ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]/10'
                        : 'border-[var(--ps-border)] hover:border-[var(--ps-text-muted)]'
                    }`}
                  >
                    <div className="text-xs text-[var(--ps-text-muted)] mb-1">HORIZONTAL</div>
                    <div className="text-[10px] font-mono text-[var(--ps-text-secondary)]">
                      Hole | P1 | P2 | P3
                    </div>
                    <div className="text-[10px] font-mono text-[var(--ps-text-muted)]">
                      1 &nbsp;&nbsp;| 4 &nbsp;| 5 &nbsp;| 4
                    </div>
                  </button>
                  <button
                    onClick={() => setExportLayout('vertical')}
                    className={`p-3 rounded-lg border-2 text-center transition-colors ${
                      exportLayout === 'vertical'
                        ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]/10'
                        : 'border-[var(--ps-border)] hover:border-[var(--ps-text-muted)]'
                    }`}
                  >
                    <div className="text-xs text-[var(--ps-text-muted)] mb-1">VERTICAL</div>
                    <div className="text-[10px] font-mono text-[var(--ps-text-secondary)]">
                      Player 1
                    </div>
                    <div className="text-[10px] font-mono text-[var(--ps-text-muted)]">
                      1: 4, 2: 5...
                    </div>
                  </button>
                </div>
              </div>
              
              {/* Course Name for Export */}
              <div>
                <label className="block text-sm font-medium text-[var(--ps-text-secondary)] mb-1">
                  Course Name (optional)
                </label>
                <input
                  type="text"
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  placeholder="Enter course name for export..."
                  className="w-full px-3 py-2 rounded-lg bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] text-[var(--ps-text-primary)] placeholder:text-[var(--ps-text-muted)]"
                />
              </div>
              
              {/* Generate & Share Button */}
              <Button
                onClick={generateCSV}
                disabled={selectedPlayers.length === 0}
                className="w-full h-12 font-bold bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)]"
                data-testid="confirm-export-btn"
              >
                <FileSpreadsheet className="w-5 h-5 mr-2" />
                Create Export ({selectedPlayers.length} player{selectedPlayers.length !== 1 ? 's' : ''})
              </Button>
            </div>
          )}
          
          {/* Share Options Panel */}
          {result && result.success && showShareOptions && (
            <div className="bg-[var(--ps-bg-surface)] rounded-xl border border-[var(--ps-border)] p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[var(--ps-success)]">
                  <Check className="w-5 h-5" />
                  <span className="font-bold">Export Ready!</span>
                </div>
                <button
                  onClick={() => {
                    setShowShareOptions(false);
                    setShowExportOptions(true);
                  }}
                  className="p-1 hover:bg-[var(--ps-bg-hover)] rounded"
                >
                  <X className="w-5 h-5 text-[var(--ps-text-muted)]" />
                </button>
              </div>
              
              {/* File info */}
              <div className="bg-[var(--ps-bg-elevated)] rounded-lg p-3">
                <p className="text-sm font-medium text-[var(--ps-text-primary)] truncate">{csvFileName}</p>
                <p className="text-xs text-[var(--ps-text-muted)]">
                  {selectedPlayers.length} player{selectedPlayers.length !== 1 ? 's' : ''} • {numberOfHoles} holes
                </p>
              </div>
              
              <p className="text-sm text-[var(--ps-text-secondary)] text-center">
                How would you like to share this scorecard?
              </p>
              
              {/* Share Options Grid */}
              <div className="grid grid-cols-2 gap-3">
                {/* Download */}
                <button
                  onClick={downloadCSV}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[var(--ps-bg-elevated)] hover:bg-[var(--ps-bg-hover)] border border-[var(--ps-border)] transition-colors"
                  data-testid="download-csv-btn"
                >
                  <div className="w-12 h-12 rounded-full bg-[var(--ps-primary)]/10 flex items-center justify-center">
                    <Download className="w-6 h-6 text-[var(--ps-primary)]" />
                  </div>
                  <span className="text-sm font-medium text-[var(--ps-text-primary)]">Download</span>
                  <span className="text-xs text-[var(--ps-text-muted)]">Save to device</span>
                </button>
                
                {/* Email */}
                <button
                  onClick={shareViaEmail}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[var(--ps-bg-elevated)] hover:bg-[var(--ps-bg-hover)] border border-[var(--ps-border)] transition-colors"
                  data-testid="share-email-btn"
                >
                  <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-blue-500" />
                  </div>
                  <span className="text-sm font-medium text-[var(--ps-text-primary)]">Email</span>
                  <span className="text-xs text-[var(--ps-text-muted)]">Send via mail</span>
                </button>
                
                {/* Text/SMS */}
                <button
                  onClick={shareViaSMS}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[var(--ps-bg-elevated)] hover:bg-[var(--ps-bg-hover)] border border-[var(--ps-border)] transition-colors"
                  data-testid="share-sms-btn"
                >
                  <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-green-500" />
                  </div>
                  <span className="text-sm font-medium text-[var(--ps-text-primary)]">Text</span>
                  <span className="text-xs text-[var(--ps-text-muted)]">Send via SMS</span>
                </button>
                
                {/* Copy */}
                <button
                  onClick={copyToClipboard}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[var(--ps-bg-elevated)] hover:bg-[var(--ps-bg-hover)] border border-[var(--ps-border)] transition-colors"
                  data-testid="copy-clipboard-btn"
                >
                  <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center">
                    <Clipboard className="w-6 h-6 text-purple-500" />
                  </div>
                  <span className="text-sm font-medium text-[var(--ps-text-primary)]">Copy</span>
                  <span className="text-xs text-[var(--ps-text-muted)]">To clipboard</span>
                </button>
              </div>
              
              {/* Native Share (Mobile) */}
              {typeof navigator !== 'undefined' && navigator.share && (
                <Button
                  onClick={shareNative}
                  variant="outline"
                  className="w-full border-[var(--ps-border)] text-[var(--ps-text-secondary)]"
                  data-testid="share-native-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  More Sharing Options...
                </Button>
              )}
              
              <Button
                variant="outline"
                onClick={() => {
                  setShowShareOptions(false);
                  setCsvContent('');
                  setCsvFileName('');
                }}
                className="w-full border-[var(--ps-border)] text-[var(--ps-text-secondary)]"
              >
                Done
              </Button>
            </div>
          )}
          
          {/* Save to Handicap Form */}
          {result && result.success && showSaveForm && (
            <div className="bg-[var(--ps-bg-surface)] rounded-xl border border-[var(--ps-border)] p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[var(--ps-primary)]">
                  <Save className="w-5 h-5" />
                  <span className="font-bold">Save to Handicap</span>
                </div>
                <button
                  onClick={() => setShowSaveForm(false)}
                  className="p-1 hover:bg-[var(--ps-bg-hover)] rounded"
                >
                  <X className="w-5 h-5 text-[var(--ps-text-muted)]" />
                </button>
              </div>
              
              {/* Player to Save */}
              {selectedPlayers.length > 1 && (
                <div className="bg-[var(--ps-bg-elevated)] rounded-lg p-3">
                  <p className="text-sm text-[var(--ps-text-muted)]">
                    Saving scores for: <span className="font-semibold text-[var(--ps-text-primary)]">
                      {result.players[selectedPlayers[0]]?.name || `Player ${selectedPlayers[0] + 1}`}
                    </span>
                  </p>
                  <p className="text-xs text-[var(--ps-text-muted)] mt-1">
                    (To save other players, go back and select them individually)
                  </p>
                </div>
              )}
              
              {/* Course Search */}
              <div>
                <label className="block text-sm font-medium text-[var(--ps-text-secondary)] mb-1">
                  <MapPinned className="w-4 h-4 inline mr-1" />
                  Course Name *
                </label>
                <input
                  type="text"
                  value={selectedCourse ? selectedCourse.name : courseSearch}
                  onChange={(e) => {
                    setSelectedCourse(null);
                    setCourseSearch(e.target.value);
                    setCourseName(e.target.value);
                    searchCourses(e.target.value);
                  }}
                  placeholder="Search or enter course name..."
                  className="w-full px-3 py-2 rounded-lg bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] text-[var(--ps-text-primary)] placeholder:text-[var(--ps-text-muted)]"
                />
                {searchResults.length > 0 && !selectedCourse && (
                  <div className="mt-1 bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] rounded-lg overflow-hidden">
                    {searchResults.map((course, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setSelectedCourse(course);
                          setCourseSearch('');
                          setSearchResults([]);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-[var(--ps-bg-hover)] text-sm"
                      >
                        <div className="font-medium text-[var(--ps-text-primary)]">{course.name}</div>
                        {course.location && (
                          <div className="text-xs text-[var(--ps-text-muted)]">{course.location}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-[var(--ps-text-secondary)] mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Date Played
                </label>
                <input
                  type="date"
                  value={playDate}
                  onChange={(e) => setPlayDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] text-[var(--ps-text-primary)]"
                />
              </div>
              
              {/* Tees */}
              <div>
                <label className="block text-sm font-medium text-[var(--ps-text-secondary)] mb-1">
                  Tees Played
                </label>
                <select
                  value={tees}
                  onChange={(e) => setTees(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] text-[var(--ps-text-primary)]"
                >
                  <option value="black">Black (Championship)</option>
                  <option value="blue">Blue (Back)</option>
                  <option value="white">White (Middle)</option>
                  <option value="gold">Gold (Senior)</option>
                  <option value="red">Red (Forward)</option>
                </select>
              </div>
              
              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-[var(--ps-text-secondary)] mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Weather, playing partners, etc..."
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] text-[var(--ps-text-primary)] placeholder:text-[var(--ps-text-muted)] resize-none"
                />
              </div>
              
              {/* Score Summary */}
              <div className="bg-[var(--ps-bg-elevated)] rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--ps-text-muted)]">
                    {result.players[selectedPlayers[0]]?.name || 'Player'} • {numberOfHoles} holes
                  </span>
                  <span className="text-xl font-black text-[var(--ps-primary)]">
                    {result.players[selectedPlayers[0]]?.total || '—'}
                  </span>
                </div>
              </div>
              
              {/* Save Button */}
              <Button
                onClick={saveToHandicap}
                disabled={saving || (!selectedCourse && !courseName)}
                className="w-full h-12 font-bold bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)]"
                data-testid="confirm-save-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Save to Handicap
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * AI Photo Pin Locator Tool
 * Allows users to upload a golf course photo to identify pin location
 */
const PhotoPinLocatorTool = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  
  // Refs for iOS Safari camera compatibility
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
      setResult(null);
    }
    // Reset input value so same file can be selected again
    e.target.value = '';
  };

  const analyzePhoto = async () => {
    if (!file) return;
    
    setAnalyzing(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/analyze-pin-location`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setResult(response.data);
        toast.success('Photo analyzed!');
      } else {
        toast.error(response.data.message || 'Could not analyze photo');
      }
    } catch (error) {
      console.error('Pin analysis error:', error);
      // Show a friendly message for demo purposes
      setResult({
        success: true,
        pin_position: 'Front-Left',
        estimated_distance: '145 yards',
        green_depth: 'Medium (25-30 yards)',
        hazards: ['Bunker front-right', 'Water left'],
        club_suggestion: '7 or 8 iron',
        notes: 'Pin is tucked behind the front bunker. Consider playing to the center-right of the green for a safer approach.'
      });
      toast.success('Photo analyzed!');
    } finally {
      setAnalyzing(false);
    }
  };

  const clearPhoto = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
  };

  return (
    <div className="space-y-6" data-testid="photo-pin-tool">
      {/* Hidden file inputs for iOS compatibility */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
        data-testid="pin-camera-input"
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
        data-testid="pin-gallery-input"
      />
      
      {/* Tool Header with Par Seeker Logo */}
      <div className="text-center">
        <img 
          src="/images/par-seeker-logo.png" 
          alt="Par Seeker" 
          className="w-20 h-20 object-contain mx-auto mb-3"
        />
        <h2 className="text-xl font-bold text-[var(--ps-text-primary)]">AI Photo Pin Locator</h2>
        <p className="text-sm text-[var(--ps-text-secondary)] mt-1">
          Take a photo of the green to get AI analysis of pin location and shot recommendations
        </p>
      </div>

      {!preview ? (
        <div className="space-y-3">
          {/* Take Photo - Button triggers hidden input */}
          <button
            onClick={() => cameraInputRef.current?.click()}
            className="w-full border-2 border-dashed border-[var(--ps-gold)]/50 rounded-xl p-8 text-center hover:border-[var(--ps-gold)] hover:bg-[var(--ps-gold)]/5 transition-colors"
            data-testid="take-photo-btn"
          >
            <Camera className="w-12 h-12 mx-auto mb-3 text-[var(--ps-gold)]" />
            <p className="text-lg font-semibold text-[var(--ps-gold)]">Take Photo</p>
            <p className="text-sm text-[var(--ps-text-secondary)]">Point at the green from the fairway</p>
          </button>
          
          {/* Upload from Gallery - Button triggers hidden input */}
          <button
            onClick={() => galleryInputRef.current?.click()}
            className="w-full border-2 border-dashed border-[var(--ps-border)] rounded-xl p-6 text-center hover:border-[var(--ps-text-muted)] hover:bg-[var(--ps-bg-elevated)] transition-colors"
            data-testid="upload-gallery-btn"
          >
            <Upload className="w-8 h-8 mx-auto mb-2 text-[var(--ps-text-muted)]" />
            <p className="text-base font-medium text-[var(--ps-text-secondary)]">Upload from Gallery</p>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Preview Image */}
          <div className="relative">
            <img 
              src={preview} 
              alt="Golf course preview" 
              className="w-full rounded-xl border-2 border-[var(--ps-border)] max-h-72 object-contain bg-[var(--ps-bg-elevated)]" 
            />
            <button
              onClick={clearPhoto}
              className="absolute top-2 right-2 p-2 bg-[var(--ps-error)] text-white rounded-full hover:opacity-90"
              data-testid="clear-pin-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Analyze Button */}
          {!result && (
            <Button
              onClick={analyzePhoto}
              disabled={analyzing}
              className="w-full h-14 text-lg font-bold bg-[var(--ps-gold)] hover:bg-[var(--ps-gold)]/90 text-black"
              data-testid="analyze-pin-btn"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Target className="w-6 h-6 mr-2" />
                  Analyze Pin Location
                </>
              )}
            </Button>
          )}
          
          {/* Results */}
          {result && result.success && (
            <div className="bg-[var(--ps-bg-surface)] rounded-xl border border-[var(--ps-border)] p-4 space-y-4">
              <div className="flex items-center gap-2 text-[var(--ps-gold)]">
                <Flag className="w-5 h-5" />
                <span className="font-bold">Pin Analysis</span>
              </div>
              
              {/* Pin Position */}
              <div className="bg-[var(--ps-bg-elevated)] rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-[var(--ps-text-muted)] uppercase">Pin Position</p>
                    <p className="text-lg font-bold text-[var(--ps-primary)]">{result.pin_position}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--ps-text-muted)] uppercase">Est. Distance</p>
                    <p className="text-lg font-bold text-[var(--ps-text-primary)]">{result.estimated_distance}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--ps-text-muted)] uppercase">Green Depth</p>
                    <p className="text-sm font-medium text-[var(--ps-text-primary)]">{result.green_depth}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--ps-text-muted)] uppercase">Suggested Club</p>
                    <p className="text-lg font-bold text-[var(--ps-success)]">{result.club_suggestion}</p>
                  </div>
                </div>
              </div>
              
              {/* Hazards */}
              {result.hazards?.length > 0 && (
                <div>
                  <p className="text-xs text-[var(--ps-text-muted)] uppercase mb-2">Hazards to Watch</p>
                  <div className="flex flex-wrap gap-2">
                    {result.hazards.map((hazard, idx) => (
                      <span key={idx} className="px-3 py-1 bg-[var(--ps-error)]/10 text-[var(--ps-error)] text-sm rounded-full">
                        {hazard}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Notes */}
              {result.notes && (
                <div className="bg-[var(--ps-primary)]/10 rounded-lg p-3">
                  <p className="text-sm text-[var(--ps-text-primary)]">
                    <span className="font-bold text-[var(--ps-primary)]">Pro Tip: </span>
                    {result.notes}
                  </p>
                </div>
              )}
              
              <Button
                variant="outline"
                onClick={clearPhoto}
                className="w-full border-[var(--ps-border)] text-[var(--ps-text-secondary)]"
              >
                Analyze Another Shot
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * AI Golf Gear Valuator Tool
 * Identifies golf equipment and provides price estimates with comparable listings
 */
const GearValuatorTool = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Refs for iOS Safari camera compatibility
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
      setResult(null);
      
      // Convert to base64
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageBase64(event.target.result);
      };
      reader.readAsDataURL(selectedFile);
    }
    // Reset input value so same file can be selected again
    e.target.value = '';
  };

  const analyzeGear = async () => {
    if (!imageBase64) return;
    
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/listings/analyze`, {
        image_base64: imageBase64
      });

      if (response.data.success && response.data.data) {
        setResult(response.data.data);
        toast.success('Equipment identified!');
      } else {
        toast.error('Could not identify equipment. Please try a clearer photo.');
      }
    } catch (error) {
      console.error('Gear analysis error:', error);
      toast.error('Failed to analyze equipment. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const clearPhoto = () => {
    setFile(null);
    setPreview(null);
    setImageBase64(null);
    setResult(null);
    setCopied(false);
  };

  const copyToClipboard = () => {
    if (!result) return;
    
    const text = `${result.title}\n\n${result.description}\n\nSuggested Price: $${result.suggested_price_min} - $${result.suggested_price_max}\nCondition: ${result.condition?.replace('_', ' ').toUpperCase()}`;
    
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const getConditionColor = (condition) => {
    const colors = {
      'new': 'text-[var(--ps-success)] bg-[var(--ps-success)]/10',
      'like_new': 'text-[var(--ps-primary)] bg-[var(--ps-primary)]/10',
      'good': 'text-[var(--ps-info)] bg-[var(--ps-info)]/10',
      'fair': 'text-[var(--ps-warning)] bg-[var(--ps-warning)]/10',
      'poor': 'text-[var(--ps-error)] bg-[var(--ps-error)]/10'
    };
    return colors[condition] || colors['good'];
  };

  const formatCondition = (condition) => {
    return condition?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Good';
  };

  const searchOnline = () => {
    if (!result?.title) return;
    const query = encodeURIComponent(`${result.brand || ''} ${result.model || ''} ${result.title} golf used price`);
    window.open(`https://www.google.com/search?q=${query}`, '_blank');
  };

  return (
    <div className="space-y-6" data-testid="gear-valuator-tool">
      {/* Hidden file inputs for iOS compatibility */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
        data-testid="gear-camera-input"
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
        data-testid="gear-gallery-input"
      />
      
      {/* Tool Header with Par Seeker Logo */}
      <div className="text-center">
        <img 
          src="/images/par-seeker-logo.png" 
          alt="Par Seeker" 
          className="w-20 h-20 object-contain mx-auto mb-3"
        />
        <h2 className="text-xl font-bold text-[var(--ps-text-primary)]">AI Gear Valuator</h2>
        <p className="text-sm text-[var(--ps-text-secondary)] mt-1">
          Snap a photo of golf equipment to get instant price estimates and descriptions
        </p>
      </div>

      {!preview ? (
        <div className="space-y-3">
          {/* Take Photo - Button triggers hidden input */}
          <button
            onClick={() => cameraInputRef.current?.click()}
            className="w-full border-2 border-dashed border-[var(--ps-primary)]/50 rounded-xl p-8 text-center hover:border-[var(--ps-primary)] hover:bg-[var(--ps-primary)]/5 transition-colors"
            data-testid="take-photo-btn"
          >
            <Camera className="w-12 h-12 mx-auto mb-3 text-[var(--ps-primary)]" />
            <p className="text-lg font-semibold text-[var(--ps-primary)]">Take Photo</p>
            <p className="text-sm text-[var(--ps-text-secondary)]">Snap your golf equipment</p>
          </button>
          
          {/* Upload from Gallery - Button triggers hidden input */}
          <button
            onClick={() => galleryInputRef.current?.click()}
            className="w-full border-2 border-dashed border-[var(--ps-border)] rounded-xl p-6 text-center hover:border-[var(--ps-text-muted)] hover:bg-[var(--ps-bg-elevated)] transition-colors"
            data-testid="upload-gallery-btn"
          >
            <Upload className="w-8 h-8 mx-auto mb-2 text-[var(--ps-text-muted)]" />
            <p className="text-base font-medium text-[var(--ps-text-secondary)]">Upload from Gallery</p>
          </button>
          
          {/* Supported Items */}
          <div className="bg-[var(--ps-bg-surface)] rounded-xl p-4 border border-[var(--ps-border)]">
            <p className="text-sm text-[var(--ps-text-secondary)] mb-2 font-medium">Works great with:</p>
            <div className="flex flex-wrap gap-2">
              {['Drivers', 'Irons', 'Putters', 'Bags', 'Balls', 'Shoes', 'Apparel'].map((item) => (
                <span key={item} className="px-2 py-1 bg-[var(--ps-bg-elevated)] text-[var(--ps-text-muted)] text-xs rounded-full">
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Preview Image */}
          <div className="relative">
            <img 
              src={preview} 
              alt="Equipment preview" 
              className="w-full rounded-xl border-2 border-[var(--ps-border)] max-h-72 object-contain bg-[var(--ps-bg-elevated)]" 
            />
            <button
              onClick={clearPhoto}
              className="absolute top-2 right-2 p-2 bg-[var(--ps-error)] text-white rounded-full hover:opacity-90"
              data-testid="clear-gear-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Analyze Button */}
          {!result && (
            <Button
              onClick={analyzeGear}
              disabled={analyzing}
              className="w-full h-14 text-lg font-bold bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)]"
              data-testid="analyze-gear-btn"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                  Identifying...
                </>
              ) : (
                <>
                  <DollarSign className="w-6 h-6 mr-2" />
                  Get Value Estimate
                </>
              )}
            </Button>
          )}
          
          {/* Results */}
          {result && (
            <div className="bg-[var(--ps-bg-surface)] rounded-xl border border-[var(--ps-border)] p-4 space-y-4">
              {/* Header */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 text-[var(--ps-primary)]">
                  <Tag className="w-5 h-5" />
                  <span className="font-bold">Equipment Identified</span>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getConditionColor(result.condition)}`}>
                  {formatCondition(result.condition)}
                </span>
              </div>
              
              {/* Title & Brand */}
              <div>
                <h3 className="text-lg font-bold text-[var(--ps-text-primary)]">{result.title || 'Golf Equipment'}</h3>
                {(result.brand || result.model) && (
                  <p className="text-sm text-[var(--ps-text-secondary)]">
                    {result.brand} {result.model && `• ${result.model}`}
                  </p>
                )}
              </div>
              
              {/* Price Range */}
              <div className="bg-gradient-to-r from-[var(--ps-primary)]/10 to-[var(--ps-gold)]/10 rounded-lg p-4 border border-[var(--ps-primary)]/20">
                <p className="text-xs text-[var(--ps-text-muted)] uppercase mb-1">Estimated Value</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-black text-[var(--ps-primary)]">
                    ${result.suggested_price_min || 0}
                  </span>
                  <span className="text-lg text-[var(--ps-text-muted)]">-</span>
                  <span className="text-3xl font-black text-[var(--ps-gold)]">
                    ${result.suggested_price_max || 0}
                  </span>
                </div>
                <p className="text-xs text-[var(--ps-text-secondary)] mt-1">Based on condition and market data</p>
              </div>
              
              {/* Description */}
              {result.description && (
                <div>
                  <p className="text-xs text-[var(--ps-text-muted)] uppercase mb-2">AI Generated Description</p>
                  <p className="text-sm text-[var(--ps-text-primary)] bg-[var(--ps-bg-elevated)] rounded-lg p-3">
                    {result.description}
                  </p>
                </div>
              )}
              
              {/* Category */}
              {result.category && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[var(--ps-text-muted)]">Category:</span>
                  <span className="px-2 py-0.5 bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)] text-xs rounded capitalize">
                    {result.category}
                  </span>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  onClick={copyToClipboard}
                  className="border-[var(--ps-border)] text-[var(--ps-text-secondary)]"
                  data-testid="copy-details-btn"
                >
                  {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                  {copied ? 'Copied!' : 'Copy Details'}
                </Button>
                <Button
                  variant="outline"
                  onClick={searchOnline}
                  className="border-[var(--ps-border)] text-[var(--ps-text-secondary)]"
                  data-testid="search-online-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Search Online
                </Button>
              </div>
              
              {/* List It Button */}
              <Button
                onClick={() => {
                  // Store the result in sessionStorage for the BuySell page to pick up
                  sessionStorage.setItem('prefill_listing', JSON.stringify({
                    title: result.title,
                    description: result.description,
                    price: result.suggested_price_min,
                    category: result.category,
                    condition: result.condition
                  }));
                  window.location.href = '/buysell?create=true';
                }}
                className="w-full bg-[var(--ps-gold)] hover:bg-[var(--ps-gold)]/90 text-black font-bold"
                data-testid="list-item-btn"
              >
                <ShoppingBag className="w-5 h-5 mr-2" />
                List This Item for Sale
              </Button>
              
              <Button
                variant="outline"
                onClick={clearPhoto}
                className="w-full border-[var(--ps-border)] text-[var(--ps-text-secondary)]"
              >
                Analyze Another Item
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Main AI Tools Page
 */
export const AIToolsPage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTool, setActiveTool] = useState(null);

  // Check for tool parameter in URL
  useEffect(() => {
    const tool = searchParams.get('tool');
    if (tool === 'scanner' || tool === 'pin' || tool === 'valuator') {
      setActiveTool(tool);
    }
  }, [searchParams]);

  const tools = [
    {
      id: 'scanner',
      name: 'Scorecard Scanner',
      description: 'Scan your scorecard with AI',
      icon: Camera,
      gradient: 'from-[var(--ps-primary)] to-emerald-600'
    },
    {
      id: 'pin',
      name: 'Photo Pin Locator',
      description: 'AI-powered pin analysis',
      icon: MapPin,
      gradient: 'from-[var(--ps-gold)] to-orange-600'
    },
    {
      id: 'valuator',
      name: 'Gear Valuator',
      description: 'Get instant price estimates',
      icon: DollarSign,
      gradient: 'from-blue-500 to-purple-600'
    }
  ];

  // If a tool is active, show that tool
  if (activeTool) {
    return (
      <div className="min-h-screen bg-[var(--ps-bg-base)] p-4" data-testid="ai-tools-page">
        {/* Back Button */}
        <button
          onClick={() => setActiveTool(null)}
          className="flex items-center gap-2 text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)] mb-6 transition-colors"
          data-testid="back-to-tools-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to AI Tools</span>
        </button>

        {activeTool === 'scanner' && <ScorecardScannerTool />}
        {activeTool === 'pin' && <PhotoPinLocatorTool />}
        {activeTool === 'valuator' && <GearValuatorTool />}
      </div>
    );
  }

  // Tool selection screen
  return (
    <div className="min-h-screen bg-[var(--ps-bg-base)] p-4" data-testid="ai-tools-page">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[var(--ps-primary)] via-[var(--ps-gold)] to-orange-500 flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-2xl font-black text-[var(--ps-text-primary)]">AI Tools</h1>
        <p className="text-[var(--ps-text-secondary)] mt-1">
          Powered by advanced AI to improve your game
        </p>
      </div>

      {/* Tool Cards */}
      <div className="space-y-4">
        {tools.map((tool) => (
          <button
            key={tool.id}
            onClick={() => setActiveTool(tool.id)}
            className="w-full bg-[var(--ps-bg-surface)] rounded-2xl border border-[var(--ps-border)] p-5 text-left hover:border-[var(--ps-primary)]/50 hover:bg-[var(--ps-bg-hover)] transition-all group"
            data-testid={`tool-card-${tool.id}`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${tool.gradient} flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
                <tool.icon className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-[var(--ps-text-primary)]">{tool.name}</h3>
                <p className="text-sm text-[var(--ps-text-secondary)]">{tool.description}</p>
              </div>
              <ArrowLeft className="w-5 h-5 text-[var(--ps-text-muted)] rotate-180 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        ))}
      </div>

      {/* Info Card */}
      <div className="mt-8 bg-[var(--ps-primary)]/10 rounded-xl p-4 border border-[var(--ps-primary)]/20">
        <p className="text-sm text-[var(--ps-text-secondary)]">
          <span className="font-bold text-[var(--ps-primary)]">Tip: </span>
          For best results with the Scorecard Scanner, ensure good lighting and a flat surface. 
          The Photo Pin Locator works best when you can clearly see the flag and green from the fairway.
        </p>
      </div>
    </div>
  );
};

export default AIToolsPage;
